import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, DOC_TYPE_LABELS, errMsg, fmtDate, Money } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DateRangeFilter, DemoBadge, PageHeader, SearchBox, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Invoices({ fixedDocType, title, subtitle }) {
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [docType, setDocType] = useState(fixedDocType || "");
  const [range, setRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices", {
        params: { q, status: status || params.get("status") || "", doc_type: docType,
                  from_date: range.from, to_date: range.to, page, limit: 25 },
      });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [q, status, docType, range, page, params]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const baseTestid = fixedDocType === "credit_note" ? "credit-notes" : fixedDocType === "debit_note" ? "debit-notes" : "invoices";

  return (
    <div>
      <PageHeader title={title || "Invoices"}
        subtitle={subtitle || "GST tax invoices, bills of supply, receipts & proformas"}
        testid={`${baseTestid}-header`}
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }}
              placeholder="Search invoice no, customer, GSTIN…" testid={`${baseTestid}-search`} />
            {!fixedDocType && (
              <Select value={docType || "all"} onValueChange={(v) => { setDocType(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid="invoices-doctype-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-32 bg-white border-[#E5E0D8]" data-testid={`${baseTestid}-status-filter`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["all", "draft", "issued", "partial", "paid", "cancelled"].map((s) => (
                  <SelectItem key={s} value={s}>{s === "all" ? "All Status" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangeFilter from={range.from} to={range.to} testid={`${baseTestid}-date-filter`}
              onChange={(from, to) => { setRange({ from, to }); setPage(1); }} />
            {can(user?.role, "invoices", true) && (
              <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid={`${baseTestid}-create-button`}
                onClick={() => navigate(fixedDocType ? `/invoices/new?doc_type=${fixedDocType}` : "/invoices/new")}>
                <Plus className="h-4 w-4 mr-1" /> New {fixedDocType === "credit_note" ? "Credit Note" : fixedDocType === "debit_note" ? "Debit Note" : "Invoice"}
              </Button>
            )}
          </>
        } />
      <DataTable testid={`${baseTestid}-table`} loading={loading}
        columns={[
          { key: "invoice_no", label: "Number", render: (r) => (
            <span className="font-semibold text-[#0D4F4A]">{r.invoice_no || <span className="text-slate-400">DRAFT</span>}{r.is_demo && <DemoBadge />}</span>) },
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "customer", label: "Customer", render: (r) => (
            <div><div className="font-medium">{r.customer?.name}</div>
              <div className="text-xs text-[#5C6B69]">{r.customer_type}{r.customer?.gstin ? ` · ${r.customer.gstin}` : ""}</div></div>) },
          ...(fixedDocType ? [] : [{ key: "doc_type", label: "Type", render: (r) => <span className="text-xs">{DOC_TYPE_LABELS[r.doc_type] || r.doc_type}</span> }]),
          { key: "taxable_total", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable_total} /> },
          { key: "total_gst", label: "GST", align: "right", render: (r) => <Money value={r.total_gst} /> },
          { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} className="font-semibold" /> },
          { key: "balance", label: "Balance", align: "right", render: (r) => (
            <span className={`num ${r.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{r.balance > 0 ? `₹${r.balance.toLocaleString("en-IN")}` : "—"}</span>) },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} testid={`${baseTestid}-status-${r.id}`} /> },
        ]}
        rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        onRow={(r) => navigate(`/invoices/${r.id}`)}
        empty="No documents found. Create your first invoice!" />
    </div>
  );
}
